import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { isNullOrUndefined } from '@sla-ng/core';
import { IAddHourlyWage } from '../models/add-hourly-wage-model';
import { ICostCenterCostUnitDistribution } from '../models/cost-center-cost-unit-distribution.model';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { ICostCenterReferenceValueUpdate } from '../models/cost-center-reference-value-update.model';
import { ICostCenterReferenceValue } from '../models/cost-center-reference-value.model';
import { ICostCenter } from '../models/cost-center.model';
import { ICostUnitDistributionTransfer } from '../models/cost-unit-distribution-transfer.model';
import { IHourlyWage } from '../models/hourly-wage-model';
import { IReference } from '../models/reference.model';
import { ICostCenterApportionment } from '../models/cost-center-apportionment.model';
import { ICostCenterApportionmentUpdate } from '../models/cost-center-apportionment-update.model';
import { CostAccountingRequestService } from '../services/cost-accounting-request.request.service';

const DATEFORMAT: string = 'YYYY-MM-DD';
const BASE_URL: string = 'cost-center';

@Injectable({
    providedIn: 'root',
})
export class CostCenterResource {
    constructor(private myCostAccountingRequestService: CostAccountingRequestService) {
    }

    public getCostCenters(validFrom?: moment.Moment, validTo?: moment.Moment): Observable<ICostCenter[]> {
        let params: Params = {};
        if (!isNullOrUndefined(validFrom) && !isNullOrUndefined(validTo)) {
            params = { validFrom: validFrom.format(DATEFORMAT), validTo: validTo.format(DATEFORMAT) };
        }
        return this.myCostAccountingRequestService.get(BASE_URL, { params });
    }

    public createCostCenter(costCenter: ICostCenter): Observable<ICostCenter> {
        return this.myCostAccountingRequestService.post(BASE_URL, costCenter);
    }

    public updateCostCenter(costCenter: ICostCenter): Observable<ICostCenter> {
        return this.myCostAccountingRequestService.put(`${BASE_URL}/${costCenter.id}`, costCenter);
    }

    public deleteCostCenter(costCenterId: number): Observable<void> {
        return this.myCostAccountingRequestService.delete(`${BASE_URL}/${costCenterId}`);
    }

    public getCostCenterReferenceValuesForCostAccountingType(costCenterId: number, costAccountingTypeId?: number, referenceId?: number, validFrom?: moment.Moment): Observable<ICostCenterReferenceValue[]> {
        const params: Params = { };
        if (costAccountingTypeId !== undefined) {
            params[ 'costAccountingTypeId' ] = costAccountingTypeId.toString();
        }

        if (referenceId !== undefined) {
            params[ 'referenceId' ] = referenceId.toString();
        }

        if (validFrom !== undefined) {
            params[ 'validFrom' ] = validFrom.format(DATEFORMAT);
        }
        return this.myCostAccountingRequestService.get(`${BASE_URL}/${costCenterId}/reference-value`, { params });
    }

    public updateCostCenterReferenceValues(costCenterId: number, referenceValueId: number, value: ICostCenterReferenceValueUpdate): Observable<ICostCenterReferenceValue> {
        return this.myCostAccountingRequestService.patch(`${BASE_URL}/${costCenterId}/reference-value/${referenceValueId}`, value);
    }

    public addCostCenterReferenceValue(costCenterReferenceValue: ICostCenterReferenceValue): Observable<ICostCenterReferenceValue> {
        return this.myCostAccountingRequestService.post(`${BASE_URL}/${costCenterReferenceValue.costCenterId}/reference-value`, costCenterReferenceValue);
    }

    public deleteReferenceValue(referenceValue: ICostCenterReferenceValue): Observable<void> {
        return this.myCostAccountingRequestService.delete(`${BASE_URL}/${referenceValue.costCenterId}/reference-value/${referenceValue.id}`);
    }

    public addCostCenterApportionment(costCenterId: number, apportionment: ICostCenterApportionment): Observable<ICostCenterApportionment> {
        return this.myCostAccountingRequestService.post(`${BASE_URL}/${costCenterId}/apportionments`, apportionment);
    }

    public getCostCenterApportionments(costCenterId: number, costAccountingType: number, validFrom: moment.Moment, validTo: moment.Moment): Observable<ICostCenterApportionment[]> {
        const params: Params = {
            costAccountingType: costAccountingType.toString(),
            validFrom: validFrom.format(DATEFORMAT),
            validTo: validTo.format(DATEFORMAT),
        };
        return this.myCostAccountingRequestService.get(`${BASE_URL}/${costCenterId}/apportionments`, { params });
    }

    public updateCostCenterApportionment(apportionmentId: number, costCenterId: number, updatedValues: ICostCenterApportionmentUpdate): Observable<ICostCenterApportionment> {
        return this.myCostAccountingRequestService.patch(`${BASE_URL}/${costCenterId}/apportionments/${apportionmentId}`, updatedValues);
    }

    public updateApportionmentReference(costCenterId: number, referenceId: number, costAccountingTypeId: number): Observable<void> {
        return this.myCostAccountingRequestService.patch(`${BASE_URL}/${costCenterId}/apportionments`, {
            referenceId,
            costAccountingTypeId,
        });
    }

    public getApportionmentReference(costCenterId: number, costAccountingTypeId: number): Observable<IReference> {
        const params: Params = { costAccountingTypeId };
        return this.myCostAccountingRequestService.get(`${BASE_URL}/${costCenterId}/apportionments/reference`, { params });
    }

    public deleteCostCenterCostApportionment(
        emittingCostCenterId: number,
        apportionmentId: number): Observable<void> {
        return this.myCostAccountingRequestService.delete(`${BASE_URL}/${emittingCostCenterId}/apportionments/${apportionmentId}`);
    }

    public getCostUnitDistributions(
        costCenterId: number,
        costAccountingTypeId: number,
        validFrom: moment.Moment,
        validTo: moment.Moment): Observable<ICostCenterCostUnitDistribution[]> {

        const params: Params = {
            costAccountingType: costAccountingTypeId,
            validFrom: validFrom.format(DATEFORMAT),
            validTo: validTo.format(DATEFORMAT),
        };
        return this.myCostAccountingRequestService.get(`${BASE_URL}/${costCenterId}/cost-unit-distributions`, { params });
    }

    public addCostCenterCostUnitDistribution(
        costCenterId: number,
        costUnitId: number,
        costAccountingTypeId: number,
        validFrom: moment.Moment,
        validTo: moment.Moment,
    ): Observable<ICostCenterCostUnitDistribution> {
        const requestBody = { costUnitId, costAccountingTypeId, validFrom, validTo };
        return this.myCostAccountingRequestService.post(`${BASE_URL}/${costCenterId}/cost-unit-distributions`, requestBody);
    }

    public deleteCostCenterCostUnitDistribution(
        costCenterId: number,
        distributionId: number): Observable<void> {
        return this.myCostAccountingRequestService.delete(`${BASE_URL}/${costCenterId}/cost-unit-distributions/${distributionId}`);
    }

    public transferCostCenterCostUnitDistribution(request: ICostUnitDistributionTransfer): Observable<void> {
        return this.myCostAccountingRequestService.post(`${BASE_URL}/transfer-cost-unit-distributions`, request);
    }

    public updateCostCenterCostUnitDistributionValidity(
        costCenterId: number,
        distributionId: number,
        validTo: moment.Moment): Observable<ICostCenterCostUnitDistribution> {
        return this.myCostAccountingRequestService.patch(`${BASE_URL}/${costCenterId}/cost-unit-distributions/${distributionId}`, { validTo });
    }

    public updateCostCenterCostUnitDistributionOrder(
        costCenterId: number,
        distributionId: number,
        calculationOrder: number): Observable<ICostCenterCostUnitDistribution> {
        return this.myCostAccountingRequestService.patch(`${BASE_URL}/${costCenterId}/cost-unit-distributions/${distributionId}/calculation-order`, { calculationOrder });
    }

    public updateCostCenterCostUnitDistributionReference(
        costCenterId: number,
        referenceId: number,
        costAccountingTypeId: number): Observable<void> {
        return this.myCostAccountingRequestService.patch(`${BASE_URL}/${costCenterId}/cost-unit-distributions`, {
            referenceId,
            costAccountingTypeId,
        });
    }

    public getCostUnitDistributionReference(
        costCenterId: number,
        costAccountingTypeId: number): Observable<IReference> {
        const params: Params = { costAccountingTypeId };
        return this.myCostAccountingRequestService.get(`${BASE_URL}/${costCenterId}/cost-unit-distributions/reference`, { params });
    }

    public getHourlyWages(costCenterId: number): Observable<IHourlyWage[]> {
        return this.myCostAccountingRequestService.get(`${BASE_URL}/${costCenterId}/hourly-wage`);
    }

    public addHourlyWage(costCenterId: number, addWage: IAddHourlyWage): Observable<IHourlyWage> {
        return this.myCostAccountingRequestService.post(`${BASE_URL}/${costCenterId}/hourly-wage`, addWage);
    }

    public deleteHourlyWage(costCenterId: number, wageId: number): Observable<void> {
        return this.myCostAccountingRequestService.delete(`${BASE_URL}/${costCenterId}/hourly-wage/${wageId}`);
    }


}
